import { localeMeta } from "@/lib/seo-head";
import { useTx } from "@/lib/i18n-dict";
import { createFileRoute } from "@tanstack/react-router";
import { Reveal } from "@/components/site/Reveal";
import { Section, SectionHeading } from "@/components/site/Section";
import { GhostCta, PrimaryCta } from "@/components/site/Cta";
import { ONBOARDING as ONBOARDING_SOURCE } from "@/content/pages";
import { alternateLinks } from "@/lib/i18n";

const TITLE = "Onboarding — a smooth, guided start";
const DESCRIPTION =
  "A structured 45–120 day onboarding program with a personal manager, data migration support and clear milestones — no army of consultants required.";

export const Route = createFileRoute("/{-$locale}/onboarding")({
  staticData: { sitemap: true },
  head: ({ params }) => {
    const __m = localeMeta(params, TITLE, DESCRIPTION);
    return ({
    meta: [
      { title: __m.title },
      { name: "description", content: __m.description },
      { property: "og:title", content: __m.title },
      { property: "og:description", content: __m.description },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: alternateLinks("/onboarding"),
  });
  },
  component: Onboarding,
});

function Onboarding() {
  const ONBOARDING = useTx(ONBOARDING_SOURCE);
  return (
    <>
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 top-0 h-96"
          style={{ background: "var(--gradient-edge)" }}
        />
        <div className="container-page relative py-20 md:py-28">
          <Reveal className="max-w-2xl">
            <p className="text-xs font-semibold tracking-[0.2em] text-primary uppercase">
              {ONBOARDING.intro.eyebrow}
            </p>
            <h1 className="mt-3 font-display text-4xl leading-[1.05] font-bold text-balance-tight sm:text-5xl">
              {ONBOARDING.intro.title}
            </h1>
            <p className="mt-6 text-base text-muted-foreground md:text-lg">
              {ONBOARDING.intro.lead}
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <PrimaryCta to="/registration-demo">Book a demo</PrimaryCta>
              <GhostCta to="/contacts">Talk to a consultant</GhostCta>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Stats */}
      <Section>
        <SectionHeading eyebrow="A methodology that works" title="Real achievements of our clients" />
        <div className="mt-12 grid gap-px overflow-hidden rounded-xl border border-border bg-border sm:grid-cols-3">
          {ONBOARDING.stats.map((stat, index) => (
            <Reveal key={stat.label} delay={index * 50} className="bg-background p-8 text-center">
              <p className="font-display text-3xl font-bold text-primary md:text-4xl">
                {stat.value}
              </p>
              <p className="mt-2 text-sm text-muted-foreground">{stat.label}</p>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* What's included */}
      <Section className="bg-surface/40">
        <SectionHeading eyebrow="What's included" title="Everything you need for a fast, guided launch" />
        <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {ONBOARDING.included.map((item, index) => (
            <Reveal
              key={item.title}
              delay={index * 50}
              className="rounded-xl border border-border p-6"
            >
              <h3 className="font-display text-base font-semibold">{item.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{item.text}</p>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* Steps */}
      <Section>
        <SectionHeading eyebrow="The program" title="5 stages — from start to success" />
        <ol className="mt-12 grid gap-6 md:grid-cols-5">
          {ONBOARDING.steps.map((step, index) => (
            <Reveal
              key={step.title}
              delay={index * 50}
              className="rounded-xl border border-border bg-surface/40 p-6"
            >
              <p className="text-xs font-semibold tracking-[0.2em] text-primary uppercase">
                0{index + 1} — {step.day}
              </p>
              <h3 className="mt-3 font-display text-base font-semibold">{step.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{step.text}</p>
            </Reveal>
          ))}
        </ol>
      </Section>

      {/* Testimonials */}
      <Section className="bg-surface/40">
        <SectionHeading eyebrow="Trusted with your first steps" title="What onboarding feels like" />
        <div className="mt-12 grid gap-6 lg:grid-cols-3">
          {ONBOARDING.testimonials.map((testimonial, index) => (
            <Reveal
              key={testimonial.name}
              delay={index * 60}
              className="flex h-full flex-col rounded-xl border border-border bg-background p-7"
            >
              <p className="flex-1 text-sm text-muted-foreground">{testimonial.quote}</p>
              <p className="mt-6 text-sm">
                <span className="font-medium">{testimonial.name}</span>
                <span className="text-muted-foreground"> — {testimonial.role}</span>
              </p>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* Closing */}
      <Section>
        <Reveal className="rounded-2xl border border-primary/30 bg-surface p-10 text-center shadow-[var(--glow-primary)] md:p-16">
          <h2 className="mx-auto max-w-2xl font-display text-3xl font-bold text-balance-tight md:text-4xl">
            {ONBOARDING.closing.title}
          </h2>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <PrimaryCta to="/registration-demo">Book a demo</PrimaryCta>
            <GhostCta to="/contacts">Talk to a consultant</GhostCta>
          </div>
        </Reveal>
      </Section>
    </>
  );
}
